/**
 * Tableau de résultats d'import Keycloak.
 * Responsabilité: afficher les résultats de recherche dans un tableau homogène
 * basé sur tanstack table, avec une action d'import par ligne.
 */

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ReactComponent as ApiIcon } from 'core/assets/images/icones/apiIcon.svg'
import { ReactComponent as UserIcon } from 'core/assets/images/icones/userIcon.svg'
import { TruncatedCell } from 'lauria/components/profiles/TruncatedCell'
import type { KeycloakImportItem } from 'lauria/types/keycloakImport'
import React from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  items: KeycloakImportItem[]
  importingIds: Set<string>
  importStatusById: Record<
    string,
    { type: 'error' | 'success'; message: string }
  >
  onImport: (item: KeycloakImportItem) => void
}

export const KeycloakImportResultsTable: React.FC<Props> = ({
  items,
  importingIds,
  importStatusById,
  onImport,
}) => {
  const { t } = useTranslation()

  const columnHelper = createColumnHelper<KeycloakImportItem>()

  const columns = [
    columnHelper.accessor('identifier', {
      header: t('profiles.nomUtilisateur'),
      cell: ({ row, getValue }) => (
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden="true"
            className="flex items-center justify-center size-[20px] shrink-0"
          >
            {row.original.type === 'COMPTE_SERVICE' ? (
              <ApiIcon />
            ) : (
              <UserIcon />
            )}
          </span>

          <TruncatedCell value={getValue() || '-'} />
        </span>
      ),
    }),

    columnHelper.accessor('label', {
      header: t('profiles.nom'),
      cell: ({ row, getValue }) => (
        <span className="inline-flex items-center">
          {row.original.type === 'COMPTE_SERVICE' ? (
            <span className="text-gray-600 dark:text-gray-1250"> - </span>
          ) : (
            <TruncatedCell value={getValue() || '-'} />
          )}
        </span>
      ),
    }),

    columnHelper.accessor('email', {
      header: t('profiles.email'),
      cell: ({ getValue }) => <TruncatedCell value={getValue() ?? '-'} />,
    }),

    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const item = row.original
        const isImporting = importingIds.has(item.id)
        const status = importStatusById[item.id]

        return (
          <div className="flex justify-end">
            {status ? (
              <span
                className={`text-base text-right max-w-[220px] break-words ${
                  status.type === 'error' ? 'text-red-600' : 'text-green-600'
                }`}
                title={status.message}
              >
                {status.message}
              </span>
            ) : (
              <button
                className="small-btn default text-[14px] w-[120px]"
                onClick={(e) => {
                  e.stopPropagation()
                  onImport(item)
                }}
                disabled={isImporting}
              >
                {t('profiles.importKeycloak.importButton')}
                {isImporting && (
                  <span className="button-loader ml-2 align-middle"></span>
                )}
              </button>
            )}
          </div>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (items.length === 0) {
    return (
      <div className="w-full py-10 text-center text-gray-600 dark:text-gray-1250">
        {t('profiles.importKeycloak.empty')}
      </div>
    )
  }

  return (
    <div className="overflow-auto h-full">
      <table className="table-profile-modal">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={header.id === 'actions' ? 'text-right' : undefined}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="hover:bg-gray-50 hover:dark:bg-gray-1425"
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={
                    cell.column.id === 'actions'
                      ? 'text-right pr-[20px]'
                      : undefined
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
